import { Box } from "@mui/material";
import { fundStatus } from "src/constants/common";
import { dateAndTimeStamp } from "src/utils/helper";

type StatusHistoryRow = {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  changedByRole: string;
  changedAt: string;
  remark?: string | null;
  user?: {
    id: string;
    firstName: string;
    lastName?: string;
  } | null;
};

// Transitions that send the application backwards (query / rejection) are
// visually distinguished from forward progress so the IO<->Admin to-and-fro
// reads clearly.
const SEND_BACK_STATUSES = new Set([
  "UNDER_QUERY",
  "NODAL_UNDER_QUERY",
  "REJECTED",
  "UC_REJECTED",
]);

const roleLabels: Record<string, string> = {
  IMPLEMENTING_OFFICER: "Implementing Officer",
  NODAL_OFFICER: "SNO",
  ADMIN: "Admin",
  ENTITY: "Entity",
};

const statusLabel = (status?: string | null) =>
  status ? fundStatus[status] ?? status : "";

const FundStatusTimeline = ({
  statusHistory,
}: {
  statusHistory?: StatusHistoryRow[];
}) => {
  if (!statusHistory?.length) return null;

  return (
    <Box sx={{ paddingTop: "10px" }}>
      {statusHistory.map((row) => {
        const isSendBack = SEND_BACK_STATUSES.has(row.toStatus);
        const accent = isSendBack ? "#c62828" : "#2e7d32";
        const actorName = row.user
          ? `${row.user.firstName ?? ""} ${row.user.lastName ?? ""}`.trim()
          : "";

        return (
          <Box
            key={row.id}
            sx={{
              position: "relative",
              paddingLeft: "24px",
              paddingBottom: "20px",
              borderLeft: "2px solid #e0e0e0",
              "&:last-of-type": { borderLeft: "2px solid transparent" },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: "-7px",
                top: "2px",
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: accent,
              }}
            />
            <Box
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                fontSize: "14px",
                color: accent,
              }}
            >
              {statusLabel(row.fromStatus)
                ? `${statusLabel(row.fromStatus)} → ${statusLabel(
                    row.toStatus
                  )}`
                : statusLabel(row.toStatus)}
            </Box>
            <Box
              sx={{
                fontFamily: "Poppins",
                fontSize: "12px",
                color: "#555",
                paddingTop: "2px",
              }}
            >
              {[actorName, roleLabels[row.changedByRole] ?? row.changedByRole]
                .filter(Boolean)
                .join(" · ")}
              {"  —  "}
              {dateAndTimeStamp(row.changedAt, "IST")}
            </Box>
            {row.remark && (
              <Box
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "13px",
                  color: "#333",
                  paddingTop: "4px",
                  whiteSpace: "pre-wrap",
                }}
              >
                {row.remark}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default FundStatusTimeline;
